import { makeAutoObservable, observableRef } from 'mobx';

import Collection from '@/collection/entity/Collection';
import Project from '@/hpo/entity/Project';
import Diamond from '@/product/entity/Diamond';
import Product from '@/product/entity/Product';
import { User } from '@/user/entity/User';

import { ClientSetting } from '../constants/clientSettings';
import { Permission } from '../constants/permissions';
import { Rollout } from '../constants/rollouts';

enum SecurityAction {
  GoToGrading = 'goToGradingGranted',
  CollectionEdit = 'collectionEditGranted',
  ProductEdit = 'productPageEditGranted',
  NetBox = 'netboxGranted',
  LGD = 'lgdGranted',
  Manager = 'managerGranted',
  Internal = 'internalGranted',
  ShareAccess = 'shareAccessGranted',
  Admin = 'adminGranted',
  ViewProductStatus = 'viewProductStatusGranted',
  RenderManager = 'renderManagerGranted',
  BasicGranted = 'basisGranted',
  ManageProject = 'manageProject',
  CanAccessAdminPage = 'canManageAdminPage',
  AllowCreateGasolineMovie = 'allowToCreateGasolineMovie',
  ManageContacts = 'manageContacts',
  AllowIntegralV2View = 'allowIntegralV2View',
  AllowBrillianceV2View = 'allowBrillianceV2View',
  AllowScintillationView = 'allowScintillationView',
  CanEditMultistage = 'canEditMultistage',
  StartRoughColorGrading = 'startRoughColorGrading',
  ExportCopy = 'exportCopy',
  ViewStock = 'viewStockGranted',
}

export const GO_TO_GRADING = SecurityAction.GoToGrading;
export const COLLECTION_EDIT = SecurityAction.CollectionEdit;
export const PRODUCT_EDIT = SecurityAction.ProductEdit;
export const NETBOX = SecurityAction.NetBox;
export const LGD = SecurityAction.LGD;
export const MANAGER = SecurityAction.Manager;
export const INTERNAL = SecurityAction.Internal;
export const SHARE_ACCESS = SecurityAction.ShareAccess;
export const ADMIN = SecurityAction.Admin;
export const CAN_ACCESS_ADMIN_PAGE = SecurityAction.CanAccessAdminPage;
export const VIEW_PRODUCT_STATUS = SecurityAction.ViewProductStatus;
export const RENDER_MANAGER = SecurityAction.RenderManager;
export const BASIS_GRANTED = SecurityAction.BasicGranted;
export const MANAGE_PROJECT = SecurityAction.ManageProject;
export const CREATE_GASOLINE_MOVIE = SecurityAction.AllowCreateGasolineMovie;
export const MANAGE_CONTACTS = SecurityAction.ManageContacts;
export const ALLOW_INTEGRAL_V2_VIEW = SecurityAction.AllowIntegralV2View;
export const ALLOW_BRILLIANCE_V2_VIEW = SecurityAction.AllowBrillianceV2View;
export const ALLOW_SCINTILLATION_VIEW = SecurityAction.AllowScintillationView;
export const CAN_EDIT_MULTISTAGE = SecurityAction.CanEditMultistage;
export const CAN_START_ROUGH_COLOR_GRADING = SecurityAction.StartRoughColorGrading;
export const EXPORT_COPY = SecurityAction.ExportCopy;
export const VIEW_STOCK = SecurityAction.ViewStock;

export const DENY_IMPORT_UPLOAD = 'import:upload';
export const DENY_DELETE_SOLUTION = 'solution:delete';
export const DENY_SHARE_PROJECT = 'project:acl';
export const DENY_SHARE_PRODUCT = 'product:acl';
export const DENY_DELETE_PROJECT = 'project:delete';
export const DENY_DELETE_PRODUCT = 'product:delete';
export const DENY_EDIT_PRODUCT = 'product:edit';
export const DENY_RENDER_PROJECT = 'project:render';

interface ActionOptions {
  product?: Product | Diamond;
  project?: Project;
  checkingCollection?: Nullable<Collection>;
}

const ACTION_GRANTERS: Record<SecurityAction, (user: User, options?: Nullable<ActionOptions>) => boolean> = {
  [BASIS_GRANTED]: (user) => user.hasRollout(Rollout.ProductClarityBasis),
  [GO_TO_GRADING]: (user) => user.hasRollout(Rollout.CustomGradeProducts),
  [COLLECTION_EDIT]: ({ id }, options) => Boolean(id && options?.checkingCollection?.user?.id === id),
  [PRODUCT_EDIT]: (user, options) => {
    if (!options?.product?.seller) {
      return false;
    }

    return options?.product.sellerId === user.b2bId;
  },
  [NETBOX]: (user) => user.hasPermission(Permission.ViewOwnNetbox),
  [LGD]: (user) => user.hasSetting(ClientSetting.LabGrownStones),
  [MANAGER]: (user) => user.hasPermission(Permission.ManageStock),
  [INTERNAL]: (user) => user.hasPermission(Permission.ViewSensitiveMetrics),
  [SHARE_ACCESS]: (user) => user.hasPermission(Permission.ManageStock),
  [ADMIN]: (user) => user.hasPermission(Permission.SupervisorViewAll),
  [CAN_ACCESS_ADMIN_PAGE]: (user) => Boolean(user.canAccessAdmin),
  [VIEW_PRODUCT_STATUS]: (user, options) => (ACTION_GRANTERS[MANAGER](user) &&
      ACTION_GRANTERS[PRODUCT_EDIT](user, { product: options?.product })) ||
    ACTION_GRANTERS[ADMIN](user),
  [RENDER_MANAGER]: (user) => user.hasPermission(Permission.ManageStock),
  [MANAGE_PROJECT]: (user, options) => Boolean(options?.project?.b2b?.id === user.b2bId),
  [CREATE_GASOLINE_MOVIE]: (user) => user.hasRollout(Rollout.BrilliantStory),
  [ALLOW_INTEGRAL_V2_VIEW]: (user) => user.hasRollout(Rollout.IntegralV2),
  [ALLOW_BRILLIANCE_V2_VIEW]: (user) => user.hasRollout(Rollout.BrillianceV2),
  [ALLOW_SCINTILLATION_VIEW]: (user) => user.hasRollout(Rollout.Scintillation),
  [MANAGE_CONTACTS]: (user) => Boolean(user.b2bId),
  [CAN_EDIT_MULTISTAGE]: (user) => user.hasRollout(Rollout.Multistage) && Boolean(user.b2bId),
  [CAN_START_ROUGH_COLOR_GRADING]: (user) => user.hasRollout(Rollout.RoughColorGrading),
  [EXPORT_COPY]: (user) => Boolean(user.canExportCopy),
  [VIEW_STOCK]: (user) => user.hasPermission(Permission.ViewStock),
};

export default class SecurityManager {
  user?: User;

  constructor() {
    makeAutoObservable(this, {
      user: observableRef,
    });
  }

  setUser(user: User) {
    this.user = user;
  }

  isGranted(action: SecurityAction, options: Nullable<ActionOptions> = null) {
    if (!this.user) {
      return false;
    }

    return ACTION_GRANTERS[action]?.(this.user, options) ?? null;
  }

  isAnonymous() {
    return Boolean(this.user?.isAnonymous ?? true);
  }
}

export const securityManager = new SecurityManager();
